/**
 * The assignment record — data access (migrations 959, 966, 974, 975, 983, 1028; PRD-assignment-record.md).
 *
 * Reads: the view `commitment_assignments_v` (one row per assignment: the revision in
 * force, the pending proposal, binding and plan coverage, all derived, and the latest
 * readback of the revision in force), the revision table (RLS: workspace members), and
 * `commitment_authority_coverage_v` (983: binding assignments by who authorized them).
 * Writes: the acts only — authorize_assignment, accept_assignment,
 * withdraw_assignment_revision, readback_assignment (974), dispose_assignment (975),
 * rule_commitment_continuity and supersede_commitment (983), respond_to_assignment,
 * answer_assignment_response and withdraw_assignment_response (1028) — plus the workspace switch
 * set_bind_by_hierarchy. The tables have no client write policy (build contract 2.3c,
 * one write path per act).
 * The RPCs raise on refusal; refusals surface here as { ok: false, error } carrying the
 * database's own sentence, which is already written in user language.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database, Json } from '../types/database.generated';
import type { ResponsibilityCard } from '../types/prioritization/responsibility';
import type { CommitmentPriority, CommitmentType } from '../types/prioritization/commitment';
import type { PermissionAction } from '../types/prioritization/permissions';
import { delegationFields, type Delegation } from './delegation';

export type AssignmentView = Database['public']['Views']['commitment_assignments_v']['Row'];
export type AssignmentRevision = Database['public']['Tables']['commitment_assignment_revisions']['Row'];
export type AuthorityCoverageRow = Database['public']['Views']['commitment_authority_coverage_v']['Row'];
export type AssignmentResponseRow = Database['public']['Views']['assignment_responses_v']['Row'];
export type AssignmentReadback = Database['public']['Views']['commitment_assignment_readback_v']['Row'];

/**
 * Who holds the authority that authorizes assignments, as the client can tell
 * (mirrors internal_assignment_authority_right: workspace admin or owner, or a role
 * carrying process.publish or process.legitimacy_advance). The same bar rules on a
 * commitment's continuity and supersedes it (983). The server stays the authority.
 */
export function holdsAssignmentAuthority(can: (permission: PermissionAction) => boolean): boolean {
  return can('process.publish') || can('process.legitimacy_advance') || can('admin.settings');
}

export type AssignmentVerb = 'deliver' | 'provide' | 'perform' | 'achieve' | 'maintain' | 'refrain' | 'decide';
export const ASSIGNMENT_VERBS: AssignmentVerb[] = ['deliver', 'provide', 'perform', 'achieve', 'maintain', 'refrain', 'decide'];

export interface AuthorizeAssignmentInput {
  /** Set to amend an existing assignment (a new revision); omit to establish a new one. */
  assignmentId?: string;
  assigneeUserId?: string | null;
  assigneeOrgUnitId?: string | null;
  assigneeExternal?: string | null;
  counterparty?: string | null;
  verb: AssignmentVerb;
  itemId?: string | null;
  statement?: string | null;
  dueOn?: string | null;
  quantity?: number | null;
  unit?: string | null;
  scopeStatement?: string | null;
  /** What is sufficient: an assignment is judged met against this. */
  acceptanceStandard?: string | null;
  /** What would be success, beyond what is sufficient (1028). */
  successStandard?: string | null;
  /** The trigger clause (1028): the date an answer is needed by, and what follows if none comes. */
  respondBy?: string | null;
  ifNoResponse?: string | null;
  /** What is in play (1028). */
  grounds?: AssignmentGround[];
  externalEvidenceRef?: string | null;
  /** Required on an amendment: why this revision exists. */
  basis?: string | null;
  rationale?: string | null;
  /** FR-3: the authority confirms the assignment can later be judged met or not met. */
  determinateConfirmed: boolean;
  /**
   * How much is delegated (1036). Left out, an amendment keeps the level of what it
   * amends; null takes the level off; a value names it.
   */
  delegation?: Delegation | null;
}

export interface AuthorizeResult {
  assignment_id: string;
  revision_id: string;
  revision_no: number;
  standing: 'proposed' | 'binding';
  binding_basis: 'accepted' | 'hierarchy' | 'external_evidence' | null;
  decision_id: string;
  acceptance_required: boolean;
}

export type WriteResult<T> = { ok: true; data: T } | { ok: false; error: string };

/** The five dispositions dispose_assignment performs (975). Transfer and amend are new revisions through authorize_assignment. */
export type AssignmentDispositionAct = 'suspend' | 'resume' | 'release' | 'fulfill' | 'invalidate';

export interface ReadbackResult {
  readback_id: string;
  assignment_id: string;
  revision_id: string;
  answered_as: 'assignee' | 'assignee_unit';
  created_at: string;
}

export interface DisposeResult {
  assignment_id: string;
  disposition: 'open' | 'suspended' | 'released' | 'fulfilled' | 'invalidated';
  was: string;
  act: AssignmentDispositionAct;
  decision_id: string;
  answered_as: string;
  withdrawn_revision_id: string | null;
}

function client() {
  return getTypedSupabaseClient();
}

export async function listAssignmentsForCommitment(commitmentId: string): Promise<AssignmentView[]> {
  const { data, error } = await client()
    .from('commitment_assignments_v')
    .select('*')
    .eq('commitment_id', commitmentId)
    .order('authorized_at', { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentView[];
}

export async function listAssignmentsForItem(itemId: string): Promise<AssignmentView[]> {
  const { data, error } = await client()
    .from('commitment_assignments_v')
    .select('*')
    .eq('item_id', itemId);
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentView[];
}

export async function listAssignmentRevisions(assignmentId: string): Promise<AssignmentRevision[]> {
  const { data, error } = await client()
    .from('commitment_assignment_revisions')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('revision_no', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentRevision[];
}

/** authorize_assignment's / authorize_request's p_fields, from the form's input. */
function authorizeFields(input: AuthorizeAssignmentInput): Record<string, unknown> {
  return {
    assignment_id: input.assignmentId ?? null,
    assignee_user_id: input.assigneeUserId ?? null,
    assignee_org_unit_id: input.assigneeOrgUnitId ?? null,
    assignee_external: input.assigneeExternal ?? null,
    counterparty: input.counterparty ?? null,
    verb: input.verb,
    item_id: input.itemId ?? null,
    statement: input.statement ?? null,
    due_on: input.dueOn ?? null,
    quantity: input.quantity ?? null,
    unit: input.unit ?? null,
    scope_statement: input.scopeStatement ?? null,
    acceptance_standard: input.acceptanceStandard ?? null,
    success_standard: input.successStandard ?? null,
    respond_by: input.respondBy ?? null,
    if_no_response: input.ifNoResponse ?? null,
    grounds: (input.grounds ?? []).map(({ kind, id }) => ({ kind, id })),
    external_evidence_ref: input.externalEvidenceRef ?? null,
    basis: input.basis ?? null,
    rationale: input.rationale ?? null,
    determinate_confirmed: input.determinateConfirmed,
    ...(input.delegation === undefined ? {} : input.delegation === null ? { delegation_level: null } : delegationFields(input.delegation)),
  };
}

export async function authorizeAssignment(commitmentId: string, input: AuthorizeAssignmentInput): Promise<WriteResult<AuthorizeResult>> {
  const fields = authorizeFields(input);
  const { data, error } = await client().rpc('authorize_assignment', { p_commitment_id: commitmentId, p_fields: fields as unknown as Json });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as AuthorizeResult };
}

/** Where a request lives (migration 1054). A statement home names no record. */
export type RequestHomeType = 'commitment' | 'item' | 'cycle' | 'track' | 'session' | 'statement';
export interface RequestHome {
  type: RequestHomeType;
  /** The home record; null for a request that stands on its own. */
  id: string | null;
  /** Required when the home is a statement (no record to read it from). */
  workspaceId?: string | null;
}

/** authorize_request (1054): the same act as authorizeAssignment, for a request that lives anywhere. */
export async function authorizeRequest(home: RequestHome, input: AuthorizeAssignmentInput): Promise<WriteResult<AuthorizeResult>> {
  if (home.type === 'commitment' && home.id) return authorizeAssignment(home.id, input);
  const { data, error } = await client().rpc('authorize_request', {
    p_home_type: home.type,
    // The function takes no default here: a request standing on its own sends null.
    p_home_id: (home.type === 'statement' ? null : home.id) as unknown as string,
    p_fields: authorizeFields(input) as unknown as Json,
    p_workspace_id: home.workspaceId ?? undefined,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as AuthorizeResult };
}

/** Said when a request is made from somewhere other than its home's own panel (Ask), so that panel reloads. */
export const REQUESTS_CHANGED_EVENT = 'pp:requests-changed';
export function announceRequestsChanged(home: RequestHome): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REQUESTS_CHANGED_EVENT, { detail: { type: home.type, id: home.id } }));
}

/** The requests that live on one record (or, for 'statement', every request standing on its own in a workspace). */
export async function listAssignmentsForHome(home: RequestHome): Promise<AssignmentView[]> {
  let q = client().from('commitment_assignments_v').select('*').eq('home_type', home.type);
  q = home.id ? q.eq('home_id', home.id) : q.is('home_id', null);
  if (home.workspaceId) q = q.eq('workspace_id', home.workspaceId);
  const { data, error } = await q.order('authorized_at', { ascending: false, nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentView[];
}

export async function acceptAssignment(revisionId: string, note?: string | null): Promise<WriteResult<{ revision_id: string }>> {
  const { data, error } = await client().rpc('accept_assignment', { p_revision_id: revisionId, p_note: note ?? undefined });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { revision_id: string } };
}

export async function withdrawAssignmentRevision(revisionId: string, basis: string): Promise<WriteResult<{ revision_id: string }>> {
  const { data, error } = await client().rpc('withdraw_assignment_revision', { p_revision_id: revisionId, p_basis: basis });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { revision_id: string } };
}

/** Readback (974): the bound party states what they will do differently. Never changes standing. */
export async function readbackAssignment(assignmentId: string, text: string): Promise<WriteResult<ReadbackResult>> {
  const { data, error } = await client().rpc('readback_assignment', { p_assignment_id: assignmentId, p_text: text });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as ReadbackResult };
}

/** Disposition (975): suspend, resume, release, fulfill or invalidate, with a basis; writes a Decision. */
export async function disposeAssignment(assignmentId: string, act: AssignmentDispositionAct, basis: string): Promise<WriteResult<DisposeResult>> {
  const { data, error } = await client().rpc('dispose_assignment', { p_assignment_id: assignmentId, p_disposition: act, p_basis: basis });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as DisposeResult };
}

// ── responses (1028): the card's five, in one place ─────────────────────────

/**
 * Harry Max's Requests & Agreements card, the module `requests_and_agreements` (1029). A
 * request is answered four ways; the first, Promise, is accept_assignment. A promise
 * already made is changed a fifth way, and closed by reporting it done, which the person
 * who asked confirms. The data word for "Commit to Commit" stays the card's; the control
 * says what it does, because "commit" is reserved for the Commitment.
 */
export const REQUESTS_AND_AGREEMENTS_MODULE = 'requests_and_agreements';

export type AssignmentResponseKind = 'decline' | 'counteroffer' | 'commit_to_commit' | 'renegotiate' | 'report_done' | 'escalate';
export type AssignmentResponseAnswer = 'accept' | 'decline';

export type AssignmentGroundKind = 'signal' | 'item' | 'commitment' | 'assignment' | 'decision' | 'desired_outcome_brief';

/** What is in play: an object already on the record, cited by a response or a revision. */
export interface AssignmentGround {
  kind: AssignmentGroundKind;
  id: string;
  label?: string | null;
}

export const GROUND_KIND_WORDS: Record<AssignmentGroundKind, string> = {
  signal: 'Signal',
  item: 'Item',
  commitment: 'Commitment',
  assignment: 'Assignment',
  decision: 'Decision',
  desired_outcome_brief: 'Desired outcome brief',
};

/** Terms a person offers: only the ones that differ. A term left out keeps what it was. */
export interface OfferedTerms {
  due_on?: string;
  quantity?: number;
  unit?: string;
  scope_statement?: string;
  acceptance_standard?: string;
  success_standard?: string;
  /** 1036: a different level, and the brief's lines that go with it. */
  delegation_level?: number;
  delegation_risk?: string;
  delegation_reversible?: boolean;
  delegation_above_reason?: string | null;
  delegation_may?: string | null;
  delegation_may_not?: string | null;
  delegation_come_back_if?: string | null;
  delegation_report_back?: string | null;
}

export interface RespondInput {
  basis: string;
  helpOffered?: string | null;
  getBackBy?: string | null;
  terms?: OfferedTerms | null;
  grounds?: AssignmentGround[];
  /** Escalate (1036): the boundary that was hit. */
  escalationTrigger?: string | null;
  /** Report done (1036): a link to the work. */
  reportLink?: string | null;
}

export interface RespondResult {
  response_id: string;
  response: AssignmentResponseKind;
  assignment_id: string;
  revision_id: string;
  standing: string;
  answered_as: 'assignee' | 'assignee_unit';
  terms: OfferedTerms | null;
}

export interface AnswerResult {
  response_id: string;
  outcome: string;
  assignment_id: string;
  revision_id: string | null;
  decision_id: string;
}

/** What the parties said on these assignments (any home), oldest first. */
export async function listAssignmentResponses(assignmentIds: readonly string[]): Promise<AssignmentResponseRow[]> {
  if (assignmentIds.length === 0) return [];
  const { data, error } = await client()
    .from('assignment_responses_v')
    .select('*')
    .in('assignment_id', [...assignmentIds])
    .order('said_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentResponseRow[];
}

/** The party asked says something other than yes. No Decision: nobody has decided yet. */
export async function respondToAssignment(revisionId: string, response: AssignmentResponseKind, input: RespondInput): Promise<WriteResult<RespondResult>> {
  const fields = {
    basis: input.basis,
    help_offered: input.helpOffered ?? null,
    get_back_by: input.getBackBy ?? null,
    terms: input.terms ?? null,
    grounds: (input.grounds ?? []).map(({ kind, id }) => ({ kind, id })),
    escalation_trigger: input.escalationTrigger ?? null,
    report_link: input.reportLink ?? null,
  };
  const { data, error } = await client().rpc('respond_to_assignment', {
    p_revision_id: revisionId,
    p_response: response,
    p_fields: fields as unknown as Json,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as RespondResult };
}

/** The authority answers a response; writes a Decision. Accepting offered terms makes them binding. */
export async function answerAssignmentResponse(responseId: string, answer: AssignmentResponseAnswer, basis?: string | null): Promise<WriteResult<AnswerResult>> {
  const { data, error } = await client().rpc('answer_assignment_response', {
    p_response_id: responseId,
    p_answer: answer,
    p_basis: basis ?? undefined,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as AnswerResult };
}

export async function withdrawAssignmentResponse(responseId: string, basis: string): Promise<WriteResult<{ response_id: string }>> {
  const { data, error } = await client().rpc('withdraw_assignment_response', { p_response_id: responseId, p_basis: basis });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { response_id: string } };
}

/** What can be cited as in play in a workspace: open signals first, those on the subject item before the rest, newest first in each kind. */
export async function listGroundCandidates(workspaceId: string, itemId?: string | null): Promise<AssignmentGround[]> {
  const db = client();
  const [signals, commitments, decisions, briefs] = await Promise.all([
    db.from('signal_events').select('id, message, occurrence, status, resolved_at, item_id').eq('workspace_id', workspaceId)
      .neq('status', 'dismissed').is('resolved_at', null).order('created_at', { ascending: false }).limit(50),
    db.from('commitments').select('id, title').eq('workspace_id', workspaceId).order('created_at', { ascending: false }).limit(50),
    db.from('decisions').select('id, title').eq('workspace_id', workspaceId).order('created_at', { ascending: false }).limit(50),
    db.from('desired_outcome_briefs').select('id, title').eq('workspace_id', workspaceId).order('created_at', { ascending: false }).limit(50),
  ]);
  const failed = signals.error ?? commitments.error ?? decisions.error ?? briefs.error;
  if (failed) throw new Error(failed.message);
  const open = signals.data ?? [];
  return [
    ...[...open.filter((s) => itemId && s.item_id === itemId), ...open.filter((s) => !itemId || s.item_id !== itemId)]
      .map((s) => ({ kind: 'signal' as const, id: s.id, label: s.message || s.occurrence })),
    ...(briefs.data ?? []).map((b) => ({ kind: 'desired_outcome_brief' as const, id: b.id, label: b.title })),
    ...(commitments.data ?? []).map((c) => ({ kind: 'commitment' as const, id: c.id, label: c.title })),
    ...(decisions.data ?? []).map((d) => ({ kind: 'decision' as const, id: d.id, label: d.title })),
  ];
}

export interface ResponseWords {
  /** The control's label. */
  act: string;
  /** The modal's title. */
  title: string;
  /** What it means and what it does. */
  meaning: string;
  /** The basis field's label. */
  basisLabel: string;
}

export const RESPONSE_WORDS: Record<AssignmentResponseKind, ResponseWords> = {
  decline: {
    act: 'Decline',
    title: 'Decline this request',
    meaning: 'You are saying no, and why. The request ends. Anything already in force stays in force.',
    basisLabel: 'What makes it not possible',
  },
  counteroffer: {
    act: 'Counteroffer',
    title: 'Make a counteroffer',
    meaning: 'You can’t do this as asked, and you are offering something you can do. The person who asked accepts it, declines it or asks again.',
    basisLabel: 'Why it can’t be done as asked',
  },
  commit_to_commit: {
    act: 'Get back by a date',
    title: 'Get back by a date',
    meaning: 'You need more before you can say yes or no. Say what you need and when you will answer. The request stays open, and nothing binds you yet.',
    basisLabel: 'What you need to find out',
  },
  renegotiate: {
    act: 'Renegotiate',
    title: 'Renegotiate this promise',
    meaning: 'You said you would do this and now you realize you won’t be able to. Say so now. The promise stays in force until the person who asked answers.',
    basisLabel: 'What changed',
  },
  report_done: {
    act: 'Report done',
    title: 'Report this done',
    meaning: 'You say it is done. The person who asked checks it and confirms, or says what is missing. Until then the promise stands.',
    basisLabel: 'What was delivered',
  },
  escalate: {
    act: 'Escalate',
    title: 'Escalate: a boundary was hit',
    meaning: 'Something came up that is past what you were given to decide. Name the boundary and say what you need. The promise stays in force; the person who asked says go ahead, changes the terms, or asks you to stay within them.',
    basisLabel: 'What came up, and what you need',
  },
};

/** An answer date that has gone by (1032). The server reads the date in UTC, so this does too. */
export function answerDateHasPassed(respondBy: string | null | undefined, today: Date = new Date()): boolean {
  return !!respondBy && respondBy < today.toISOString().slice(0, 10);
}

/** The five parts of a clear request, in the card's order. */
export function clearRequestParts(r: { hasListener: boolean; hasAction: boolean; sufficient: string; dueOn: string }): { part: string; question: string; ok: boolean }[] {
  return [
    { part: 'Requester', question: 'Who is asking? You.', ok: true },
    { part: 'Listener', question: 'Who is being asked?', ok: r.hasListener },
    { part: 'Future action', question: 'What do you want done?', ok: r.hasAction },
    { part: 'Conditions of satisfaction', question: 'How will you know it is done?', ok: !!r.sufficient.trim() },
    { part: 'Time', question: 'By when?', ok: !!r.dueOn },
  ];
}

/** How a closed response reads. */
const OUTCOME_WORDS: Record<string, string> = {
  accepted: 'accepted',
  declined: 'declined',
  asked_again: 'answered with a new request',
  answered: 'followed up',
  promised: 'followed by a yes',
  request_withdrawn: 'closed: the request was withdrawn',
  withdrawn: 'taken back',
  released: 'closed: the assignment was released',
  fulfilled: 'closed: the assignment was fulfilled',
  invalidated: 'closed: the assignment was invalidated',
  superseded: 'closed: the commitment was superseded',
};

export function responseOutcomeWord(outcome: string | null | undefined, response?: string | null, level?: number | null): string | null {
  if (!outcome) return null;
  // A report of done is confirmed in the request's level word (1036): approved at 3, released at 4.
  if (response === 'report_done' && outcome === 'fulfilled') return level === 3 ? 'approved' : level === 4 ? 'released' : 'confirmed';
  if (response === 'report_done' && outcome === 'declined') return level === 3 ? 'not this' : level === 4 ? 'sent back to revise' : 'not yet';
  if (response === 'escalate' && outcome === 'accepted') return 'go ahead';
  if (response === 'escalate' && outcome === 'declined') return 'held to the terms';
  return OUTCOME_WORDS[outcome] ?? outcome.replace(/_/g, ' ');
}

/** The open response, as a chip beside the standing: where the ball is. */
export function openResponseWord(r: Pick<AssignmentResponseRow, 'response' | 'get_back_by'>): AssignmentStandingWord {
  switch (r.response) {
    case 'decline': return { label: 'Declined', tone: 'coral' };
    case 'counteroffer': return { label: 'Counteroffer waiting', tone: 'mustard' };
    case 'renegotiate': return { label: 'Renegotiating', tone: 'mustard' };
    case 'report_done': return { label: 'Reported done', tone: 'green' };
    case 'escalate': return { label: 'Escalated', tone: 'mustard' };
    default: return { label: 'Answer coming', tone: 'slate' };
  }
}

/**
 * Whether a response waiting holds a partial release (mirrors release_assignment_in_part,
 * 1030): terms offered, accepted afterward, would put back what was released, and a report
 * of done is about the terms as they stand.
 */
export function responseHoldsRelease(r: Pick<AssignmentResponseRow, 'response' | 'terms'> | null | undefined): boolean {
  return !!r && (r.terms != null || r.response === 'report_done');
}

/** The open response on an assignment: at most one (uq_assignment_responses_one_open). */
export function openResponseOf(responses: readonly AssignmentResponseRow[], assignmentId: string | null | undefined): AssignmentResponseRow | null {
  return responses.find((r) => r.assignment_id === assignmentId && !r.outcome) ?? null;
}

/** The last response answered after the revision in force took over: what the bound party should still see. */
export function lastClosedResponseOf(responses: readonly AssignmentResponseRow[], assignmentId: string | null | undefined): AssignmentResponseRow | null {
  const closed = responses.filter((r) => r.assignment_id === assignmentId
    && (r.outcome === 'declined' || r.outcome === 'accepted' || (r.response === 'report_done' && r.outcome === 'fulfilled')));
  return closed.length ? closed[closed.length - 1] : null;
}

/** How far ahead a renegotiation was raised: the difference between renegotiating a promise and breaking it. */
export function raisedAheadWords(days: number | null | undefined): string | null {
  if (days == null) return null;
  if (days > 1) return `Raised ${days} days before it was due`;
  if (days === 1) return 'Raised the day before it was due';
  if (days === 0) return 'Raised the day it was due';
  return days === -1 ? 'Raised the day after it was due' : `Raised ${-days} days after it was due`;
}

/**
 * What this person may say to this assignment now, in the card's order (mirrors
 * respond_to_assignment; build contract §2.8: never draw one they may not use). The party
 * asked only: the assignee or someone placed in the assignee unit, never an admin for
 * them, never an outside party, never the person who made the request. A request waiting
 * takes decline, counteroffer and a date to get back by; a promise in force, with nothing
 * else waiting, is reported done or renegotiated. One open response at a time, except that
 * a date to get back by is followed up by the next answer. Draw none of it while the
 * module is off (the server refuses). With Delegation on (1036), a promise in force may
 * also be escalated. The server stays the authority.
 */
export function availableResponses(
  o: Pick<AssignmentView, 'disposition' | 'in_force_revision_id' | 'is_binding' | 'proposed_revision_id' | 'proposed_needs_acceptance' | 'proposed_by' | 'assignee_external' | 'assignee_user_id' | 'assignee_org_unit_id'>,
  open: Pick<AssignmentResponseRow, 'response'> | null,
  who: { userId: string | null | undefined; orgUnitIds?: readonly string[] },
  opts: { delegation?: boolean } = {},
): AssignmentResponseKind[] {
  if (!who.userId || o.assignee_external) return [];
  if (o.disposition !== 'open' && o.disposition !== 'suspended') return [];
  const bound = o.assignee_user_id === who.userId || (!!o.assignee_org_unit_id && !!who.orgUnitIds?.includes(o.assignee_org_unit_id));
  if (!bound) return [];
  if (open && open.response !== 'commit_to_commit') return [];
  if (o.proposed_revision_id) {
    if (o.proposed_needs_acceptance === false || o.proposed_by === who.userId) return [];
    return ['counteroffer', 'decline', 'commit_to_commit'];
  }
  if (!o.in_force_revision_id || !o.is_binding) return [];
  return opts.delegation ? ['report_done', 'escalate', 'renegotiate'] : ['report_done', 'renegotiate'];
}

/**
 * Whether this person may answer this response (mirrors answer_assignment_response):
 * the authority, never the person who said it. A date to get back by is not answered:
 * the answer is still theirs to give.
 */
export function mayAnswerResponse(
  r: Pick<AssignmentResponseRow, 'response' | 'said_by' | 'outcome'>,
  who: { mayAuthorize: boolean; userId: string | null | undefined },
): boolean {
  if (r.outcome || !who.mayAuthorize || r.response === 'commit_to_commit') return false;
  return r.said_by !== who.userId;
}

/** What a partial release takes: the words for what goes, why, and what is left. */
export interface ReleaseInPartInput {
  released: string;
  basis: string;
  /** Lower than the quantity in force. */
  quantity?: number | null;
  scopeStatement?: string | null;
  acceptanceStandard?: string | null;
}

export interface ReleaseInPartResult {
  assignment_id: string;
  revision_id: string;
  revision_no: number;
  replaced_revision_id: string;
  standing: string;
  binding_basis: string;
  decision_id: string;
  answered_as: string;
}

/** Part of an assignment is released and what is left stays binding (1024). */
export async function releaseAssignmentInPart(assignmentId: string, input: ReleaseInPartInput): Promise<WriteResult<ReleaseInPartResult>> {
  const fields: Record<string, unknown> = { released: input.released, basis: input.basis };
  if (input.quantity !== undefined && input.quantity !== null) fields.quantity = input.quantity;
  if (input.scopeStatement) fields.scope_statement = input.scopeStatement;
  if (input.acceptanceStandard) fields.acceptance_standard = input.acceptanceStandard;
  const { data, error } = await client().rpc('release_assignment_in_part', {
    p_assignment_id: assignmentId,
    p_fields: fields as unknown as Json,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as ReleaseInPartResult };
}

/**
 * Whether this person may release part of this assignment now (mirrors
 * release_assignment_in_part): the authority, a binding revision in force, nothing
 * proposed. The server stays the authority.
 */
export function mayReleaseInPart(
  o: Pick<AssignmentView, 'disposition' | 'in_force_revision_id' | 'is_binding' | 'proposed_revision_id'>,
  who: { mayAuthorize: boolean },
): boolean {
  if (!who.mayAuthorize) return false;
  if (o.disposition !== 'open' && o.disposition !== 'suspended') return false;
  return !!o.in_force_revision_id && !!o.is_binding && !o.proposed_revision_id;
}

/** Where each binding assignment's readback stands (1024). */
/** Where each of these assignments' readback stands (1024), wherever they live. */
export async function listAssignmentReadbacks(assignmentIds: readonly string[]): Promise<AssignmentReadback[]> {
  if (assignmentIds.length === 0) return [];
  const { data, error } = await client()
    .from('commitment_assignment_readback_v')
    .select('*')
    .in('assignment_id', [...assignmentIds]);
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignmentReadback[];
}

/** What has been released in part, per assignment, oldest first. */
export async function listReleasedTerms(assignmentIds: readonly string[]): Promise<Map<string, { text: string; at: string | null }[]>> {
  const out = new Map<string, { text: string; at: string | null }[]>();
  if (assignmentIds.length === 0) return out;
  const { data, error } = await client()
    .from('commitment_assignment_revisions')
    .select('assignment_id, released_terms, authorized_at')
    .in('assignment_id', [...assignmentIds])
    .not('released_terms', 'is', null)
    .order('revision_no', { ascending: true });
  if (error) throw new Error(error.message);
  for (const r of data ?? []) {
    if (!r.released_terms) continue;
    out.set(r.assignment_id, [...(out.get(r.assignment_id) ?? []), { text: r.released_terms, at: r.authorized_at }]);
  }
  return out;
}

export async function getBindByHierarchy(workspaceId: string): Promise<boolean | null> {
  const { data, error } = await client().rpc('get_bind_by_hierarchy', { p_workspace_id: workspaceId });
  if (error) return null;
  return (data as boolean | null) ?? null;
}

export async function setBindByHierarchy(workspaceId: string, enabled: boolean): Promise<WriteResult<{ enabled: boolean }>> {
  const { data, error } = await client().rpc('set_bind_by_hierarchy', { p_workspace_id: workspaceId, p_enabled: enabled });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { enabled: boolean } };
}

// ── readout words (one place, so every screen says the same thing) ──────────

export type AssignmentTone = 'green' | 'mustard' | 'slate' | 'coral';
export type AssignmentStandingWord = { label: string; tone: AssignmentTone };

/** How each disposition other than open reads (975). Suspended still binds; the others ended it. */
const DISPOSITION_READOUT: Record<string, AssignmentStandingWord> = {
  suspended: { label: 'Suspended', tone: 'mustard' },
  fulfilled: { label: 'Fulfilled', tone: 'green' },
  released: { label: 'Released', tone: 'slate' },
  invalidated: { label: 'Invalidated', tone: 'coral' },
  superseded: { label: 'Superseded', tone: 'slate' },
};

/** What the record can honestly say about an assignment's standing. */
export function assignmentStandingWord(o: Pick<AssignmentView, 'is_binding' | 'binding_basis' | 'proposed_revision_id' | 'asserted_only' | 'disposition' | 'in_force_revision_id' | 'proposed_needs_acceptance'>): AssignmentStandingWord {
  if (o.disposition && o.disposition !== 'open') {
    return DISPOSITION_READOUT[o.disposition] ?? { label: o.disposition.charAt(0).toUpperCase() + o.disposition.slice(1), tone: 'slate' };
  }
  if (o.is_binding) {
    const by = o.binding_basis === 'accepted' ? 'by acceptance' : o.binding_basis === 'hierarchy' ? 'by authority' : 'by evidence';
    return { label: `Binding ${by}`, tone: 'green' };
  }
  if (o.asserted_only) return { label: 'Asserted, no evidence', tone: 'coral' };
  if (o.proposed_revision_id && !o.in_force_revision_id) {
    return { label: o.proposed_needs_acceptance ? 'Proposed, awaiting the assignee' : 'Proposed', tone: 'mustard' };
  }
  return { label: 'Nothing in force', tone: 'slate' };
}

export function planCoverageWord(o: Pick<AssignmentView, 'plan_coverage' | 'item_id' | 'is_binding'>): string | null {
  if (!o.item_id) return null;
  if (o.plan_coverage) return 'In a plan of record';
  return o.is_binding ? 'Binding, no plan coverage' : 'No plan coverage';
}

export function assigneeLabel(o: Pick<AssignmentView, 'assignee_user_id' | 'assignee_org_unit_id' | 'assignee_external'>, names: { users?: Record<string, string>; units?: Record<string, string> }): string {
  if (o.assignee_external) return o.assignee_external;
  if (o.assignee_org_unit_id) return names.units?.[o.assignee_org_unit_id] ?? 'an org unit';
  if (o.assignee_user_id) return names.users?.[o.assignee_user_id] ?? 'a person';
  return 'nobody';
}

// ── dispositions (975): the words and who may, in one place ─────────────────

export interface DispositionWords {
  /** The control's label. */
  act: string;
  /** The modal's title. */
  title: string;
  /** What it means and what it does to the assignment. */
  meaning: string;
  /** The basis field's prompt. */
  basisPrompt: string;
  /** Whether it ends the assignment. */
  ends: boolean;
}

export const DISPOSITION_WORDS: Record<AssignmentDispositionAct, DispositionWords> = {
  suspend: {
    act: 'Suspend',
    title: 'Suspend this assignment',
    meaning: 'Performance is paused. The assignment and the revision in force are kept, and it can be resumed.',
    basisPrompt: 'Why performance is paused',
    ends: false,
  },
  resume: {
    act: 'Resume',
    title: 'Resume this assignment',
    meaning: 'Performance starts again under the revision in force.',
    basisPrompt: 'Why it starts again',
    ends: false,
  },
  fulfill: {
    act: 'Mark fulfilled',
    title: 'Mark this assignment fulfilled',
    meaning: 'It was met against its acceptance standard. You are recorded as the person who verified it, and the assignment ends.',
    basisPrompt: 'How it was verified as met',
    ends: true,
  },
  release: {
    act: 'Release',
    title: 'Release this assignment',
    meaning: 'The assignee is discharged without it being met. The assignment ends, and the record keeps why.',
    basisPrompt: 'Why it is discharged unmet',
    ends: true,
  },
  invalidate: {
    act: 'Invalidate',
    title: 'Invalidate this assignment',
    meaning: 'The authorization was defective. The assignment ends, marked invalid; authorize it again if the work should go on.',
    basisPrompt: 'What was defective in the authorization',
    ends: true,
  },
};

/** The words for a partial release, kept with the five dispositions. */
export const RELEASE_IN_PART_WORDS = {
  act: 'Release part of it',
  title: 'Release part of this assignment',
  meaning: 'Part of what is owed is discharged unmet. What is left stays binding at once, on the basis it already has, with no new acceptance. The bound party is asked to read it back again.',
};

/**
 * The dispositions this person may perform on this assignment now, in menu order
 * (build contract §2.8: never draw one they may not use). Mirrors dispose_assignment:
 * the authority may suspend an open assignment, resume a suspended one, and release,
 * fulfill or invalidate either; the assignee of the revision in force may mark it
 * fulfilled. Fulfill needs a binding revision in force. The server stays the authority.
 */
export function availableDispositions(
  o: Pick<AssignmentView, 'disposition' | 'in_force_revision_id' | 'is_binding' | 'assignee_user_id'>,
  who: { mayAuthorize: boolean; userId: string | null | undefined; askerConfirmsDone?: boolean },
): AssignmentDispositionAct[] {
  if (o.disposition !== 'open' && o.disposition !== 'suspended') return [];
  const acts: AssignmentDispositionAct[] = [];
  if (who.mayAuthorize) acts.push(o.disposition === 'open' ? 'suspend' : 'resume');
  const inForce = !!o.in_force_revision_id && !!o.is_binding;
  const iAmAssignee = !!who.userId && o.assignee_user_id === who.userId;
  // With Requests & Agreements on, the assignee reports it done and the person who asked confirms (1029).
  if (inForce && (who.mayAuthorize || (iAmAssignee && !who.askerConfirmsDone))) acts.push('fulfill');
  if (who.mayAuthorize) acts.push('release', 'invalidate');
  return acts;
}

/**
 * Whether this person may read back this assignment (mirrors readback_assignment, 974):
 * it binds, a revision is in force, and they are its assignee or placed in its assignee
 * unit. Never the authorizer or an admin on the assignee's behalf.
 */
export function mayReadBack(
  o: Pick<AssignmentView, 'disposition' | 'in_force_revision_id' | 'is_binding' | 'assignee_user_id' | 'assignee_org_unit_id'>,
  who: { userId: string | null | undefined; orgUnitIds?: readonly string[] },
): boolean {
  if (!o.in_force_revision_id || !o.is_binding) return false;
  if (o.disposition !== 'open' && o.disposition !== 'suspended') return false;
  if (who.userId && o.assignee_user_id === who.userId) return true;
  return !!o.assignee_org_unit_id && !!who.orgUnitIds?.includes(o.assignee_org_unit_id);
}

/** How a person answers a proposed revision (accept_assignment's `answered_as`). */
export type AcceptAnswer = 'assignee' | 'assignee_unit' | 'workspace_admin';

/**
 * Whether this person may accept this assignment's proposed revision, and as whom
 * (mirrors accept_assignment, 966): a revision is proposed and waits for acceptance,
 * the assignee is not an outside party, and the person is the assignee, is placed in
 * the assignee org unit, or is a workspace admin (690: an assignee who has left never
 * leaves it unanswerable). Never the person who authorized it: the view names the
 * proposal's authorizer only while nothing is in force (an amendment's authorizer is
 * on its revision, not on the view), so that case is left to the server, which stays
 * the authority. Returns null when the person may not accept.
 */
export function mayAcceptAssignment(
  o: Pick<AssignmentView, 'proposed_revision_id' | 'proposed_needs_acceptance' | 'assignee_external' | 'assignee_user_id' | 'assignee_org_unit_id' | 'in_force_revision_id' | 'authorized_by'>,
  who: { userId: string | null | undefined; orgUnitIds?: readonly string[]; isAdmin?: boolean },
): AcceptAnswer | null {
  if (!o.proposed_revision_id || o.proposed_needs_acceptance === false || o.assignee_external) return null;
  if (!who.userId) return null;
  if (!o.in_force_revision_id && o.authorized_by === who.userId) return null;
  if (o.assignee_user_id === who.userId) return 'assignee';
  if (o.assignee_org_unit_id && who.orgUnitIds?.includes(o.assignee_org_unit_id)) return 'assignee_unit';
  return who.isAdmin ? 'workspace_admin' : null;
}

/**
 * The Accept the Next-Step Engine put on an assignment ask (981): get_responsibilities
 * names the act on the card (ACCEPT_ASSIGNMENT → accept_assignment, the revision id in
 * its args), and on a response with terms offered (1028: ACCEPT_ASSIGNMENT_RESPONSE →
 * answer_assignment_response, the response id in its args). Null for any other card, or
 * one that carries no such act.
 */
export function assignmentAcceptOf(card: ResponsibilityCard | null | undefined): { revisionId: string; label: string } | { responseId: string; label: string } | null {
  if (card?.subject_type === 'assignment_response') {
    const offered = card.valid_actions.find((a) => a.action === 'ACCEPT_ASSIGNMENT_RESPONSE' && a.rpc === 'answer_assignment_response');
    const responseId = offered?.args?.p_response_id;
    if (!offered || typeof responseId !== 'string' || !responseId) return null;
    return { responseId, label: offered.label || 'Accept' };
  }
  if (!card || card.subject_type !== 'assignment_proposed') return null;
  const action = card.valid_actions.find((a) => a.action === 'ACCEPT_ASSIGNMENT' && a.rpc === 'accept_assignment');
  const revisionId = action?.args?.p_revision_id;
  if (!action || typeof revisionId !== 'string' || !revisionId) return null;
  return { revisionId, label: action.label || 'Accept' };
}

// ── continuity and supersession (983) ──────────────────────────────────────

/** A person's ruling on whether a commitment keeps its identity (D1). Never computed. */
export type ContinuityRuling = 'same' | 'different';

export const CONTINUITY_WORDS: Record<ContinuityRuling, { label: string; meaning: string }> = {
  same: {
    label: 'The same undertaking',
    meaning: 'The commitment keeps its identity. A change is made by amending its assignments.',
  },
  different: {
    label: 'A different undertaking',
    meaning: 'What follows is a new commitment. Supersede this one with a successor, and say what becomes of each open assignment.',
  },
};

/** The three questions the ruling weighs (PRD §3.5). Prompts for the person; they never decide it. */
export const CONTINUITY_PROMPTS: readonly string[] = [
  'Is the undertaking still recognizably the one that was taken on?',
  'Does the authorization still trace back to the decision that made it?',
  'Does accountability continue with the same people?',
];

export interface ContinuityResult {
  commitment_id: string;
  ruling: ContinuityRuling;
  was: ContinuityRuling | null;
  decision_id: string;
  answered_as: string;
}

/** Rule on continuity (983): writes a Decision; a standing ruling is changed by its maker or an admin. */
export async function ruleCommitmentContinuity(commitmentId: string, ruling: ContinuityRuling, basis: string): Promise<WriteResult<ContinuityResult>> {
  const { data, error } = await client().rpc('rule_commitment_continuity', { p_commitment_id: commitmentId, p_ruling: ruling, p_basis: basis });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as ContinuityResult };
}

/** What becomes of an open assignment when its commitment is superseded (canon 14: the person names one; nothing carries silently). */
export type SupersessionAct = 'transfer' | 'release' | 'fulfill' | 'invalidate';

export interface SupersessionDisposition {
  act: SupersessionAct;
  /** For a transfer with several successors (a split): which one, by position. */
  successor?: number;
  basis?: string | null;
}

export interface SuccessorInput {
  title: string;
  commitmentType?: CommitmentType;
  counterparty?: string | null;
  dueDate?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  priority?: CommitmentPriority;
  description?: string | null;
  /** Items to link to the successor. */
  itemIds?: string[];
}

export interface SupersedeResult {
  commitment_id: string;
  successor_commitment_ids: string[];
  decision_id: string;
  answered_as: string;
  dispositions: Array<{
    assignment_id: string;
    disposition: SupersessionAct;
    decision_id?: string;
    successor_commitment_id?: string;
    successor_assignment_id?: string;
    successor_revision_id?: string;
    standing?: string;
  }>;
}

export const SUPERSESSION_WORDS: Record<SupersessionAct, { label: string; meaning: string }> = {
  transfer: {
    label: 'Transfer to the successor',
    meaning: 'The same terms go to the successor as a new assignment. It binds again only when the assignee accepts it, or the authority above them binds it.',
  },
  release: { label: 'Release', meaning: 'The assignee is discharged without it being met.' },
  fulfill: { label: 'Mark fulfilled', meaning: 'It was met; you are recorded as the person who verified it.' },
  invalidate: { label: 'Invalidate', meaning: 'The authorization was defective.' },
};

/**
 * What may become of this assignment at supersession (mirrors supersede_commitment):
 * transfer, release and invalidate for any open or suspended assignment; fulfill only
 * when a binding revision is in force. An assignment that already ended takes none.
 */
export function supersessionActsFor(o: Pick<AssignmentView, 'disposition' | 'in_force_revision_id' | 'is_binding'>): SupersessionAct[] {
  if (o.disposition !== 'open' && o.disposition !== 'suspended') return [];
  const acts: SupersessionAct[] = ['transfer', 'release'];
  if (o.in_force_revision_id && o.is_binding) acts.push('fulfill');
  acts.push('invalidate');
  return acts;
}

/** The successor as supersede_commitment takes it: a key left out keeps the old commitment's value. */
export function successorPayload(s: SuccessorInput): Record<string, unknown> {
  const out: Record<string, unknown> = { title: s.title.trim() };
  if (s.commitmentType !== undefined) out.commitment_type = s.commitmentType;
  if (s.counterparty !== undefined) out.counterparty = s.counterparty;
  if (s.dueDate !== undefined) out.due_date = s.dueDate || null;
  if (s.ownerId !== undefined) out.owner_id = s.ownerId;
  if (s.ownerName !== undefined) out.owner_name = s.ownerName;
  if (s.priority !== undefined) out.priority = s.priority;
  if (s.description !== undefined) out.description = s.description;
  if (s.itemIds !== undefined) out.item_ids = s.itemIds;
  return out;
}

/**
 * Supersede (983): one successor replaces the commitment, or several split it. Every open
 * assignment needs a disposition; the act refuses otherwise, and refuses unless a ruling of
 * "different" stands from this person or an admin.
 */
export async function supersedeCommitment(
  commitmentId: string,
  successors: SuccessorInput[],
  dispositions: Record<string, SupersessionDisposition>,
  basis: string,
): Promise<WriteResult<SupersedeResult>> {
  const payload = successors.map(successorPayload);
  const { data, error } = await client().rpc('supersede_commitment', {
    p_commitment_id: commitmentId,
    p_successor: (payload.length === 1 ? payload[0] : payload) as unknown as Json,
    p_assignment_dispositions: dispositionsPayload(dispositions) as unknown as Json,
    p_basis: basis,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as SupersedeResult };
}

/** The dispositions as supersede_commitment and merge_commitments take them. */
function dispositionsPayload(dispositions: Record<string, SupersessionDisposition>): Record<string, unknown> {
  const disp: Record<string, unknown> = {};
  for (const [assignmentId, d] of Object.entries(dispositions)) {
    disp[assignmentId] = d.successor === undefined && !d.basis
      ? d.act
      : { disposition: d.act, ...(d.successor !== undefined ? { successor: d.successor } : {}), ...(d.basis ? { basis: d.basis } : {}) };
  }
  return disp;
}

export interface MergeResult {
  successor_commitment_id: string;
  merged_commitment_ids: string[];
  decision_id: string;
  answered_as: string;
  dispositions: SupersedeResult['dispositions'];
}

/**
 * Merge (1024): several commitments into one new one. Each needs a ruling of "different"
 * from this person or an admin, and every open assignment on each needs a disposition;
 * the act refuses whole otherwise. With no itemIds, the successor holds every item they held.
 */
export async function mergeCommitments(
  commitmentIds: string[],
  successor: SuccessorInput,
  dispositions: Record<string, SupersessionDisposition>,
  basis: string,
): Promise<WriteResult<MergeResult>> {
  const { data, error } = await client().rpc('merge_commitments', {
    p_commitment_ids: commitmentIds,
    p_successor: successorPayload(successor) as unknown as Json,
    p_assignment_dispositions: dispositionsPayload(dispositions) as unknown as Json,
    p_basis: basis,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as MergeResult };
}

export interface ContinuityStanding {
  status: string;
  continuityRuling: ContinuityRuling | null;
  continuityRuledBy: string | null;
}

/** Whether this person may rule now (mirrors rule_commitment_continuity). */
export function mayRuleOnContinuity(c: ContinuityStanding, who: { mayAuthorize: boolean; isAdmin: boolean; userId: string | null | undefined }): boolean {
  if (!who.mayAuthorize) return false;
  if (c.status !== 'active' && c.status !== 'suspended') return false;
  if (!c.continuityRuling) return true;
  return who.isAdmin || (!!who.userId && c.continuityRuledBy === who.userId);
}

/** Whether this person may supersede now (mirrors supersede_commitment): a "different" ruling stands, by them or they are an admin. */
export function maySupersede(c: ContinuityStanding, who: { mayAuthorize: boolean; isAdmin: boolean; userId: string | null | undefined }): boolean {
  if (!who.mayAuthorize) return false;
  if (c.status !== 'active' && c.status !== 'suspended') return false;
  if (c.continuityRuling !== 'different') return false;
  return who.isAdmin || (!!who.userId && c.continuityRuledBy === who.userId);
}

/**
 * The commitments this one may be merged with now: each active or suspended, ruled
 * different, by this person or they are an admin (mirrors merge_commitments).
 */
export function mergeCandidates<T extends ContinuityStanding & { id: string; supersededByCommitmentId?: string | null }>(
  commitment: T,
  all: readonly T[],
  who: { mayAuthorize: boolean; isAdmin: boolean; userId: string | null | undefined },
): T[] {
  if (!maySupersede(commitment, who)) return [];
  return all.filter((c) => c.id !== commitment.id && maySupersede(c, who));
}

// ── authority coverage (983, PRD Phase 3) ──────────────────────────────────

export async function listAuthorityCoverage(workspaceId: string): Promise<AuthorityCoverageRow[]> {
  const { data, error } = await client()
    .from('commitment_authority_coverage_v')
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) throw new Error(error.message);
  return (data ?? []) as AuthorityCoverageRow[];
}

export interface AuthorityCounts {
  binding: number;
  byHolder: number;
  byAdmin: number;
  noRightConfigured: number;
  unstamped: number;
  /** Is the right held by anyone other than an admin in the workspace now. */
  rightsConfigured: boolean | null;
  /** Binding assignments whose readback is unresolved (1024). */
  readbackUnresolved: number;
}

export function coverageCounts(row: AuthorityCoverageRow, scope: 'commitment' | 'workspace'): AuthorityCounts {
  const n = (v: number | null | undefined) => Number(v ?? 0);
  return scope === 'commitment'
    ? { binding: n(row.binding_count), byHolder: n(row.by_right_holder_count), byAdmin: n(row.by_admin_count),
        noRightConfigured: n(row.no_right_configured_count), unstamped: n(row.unstamped_count), rightsConfigured: row.rights_configured,
        readbackUnresolved: n(row.readback_unresolved_count) }
    : { binding: n(row.ws_binding_count), byHolder: n(row.ws_by_right_holder_count), byAdmin: n(row.ws_by_admin_count),
        noRightConfigured: n(row.ws_no_right_configured_count), unstamped: n(row.ws_unstamped_count), rightsConfigured: row.rights_configured,
        readbackUnresolved: n(row.ws_readback_unresolved_count) };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The authority readout in words (PRD §3.6): of the binding assignments, how many were
 * authorized by a holder of the right, read from the stamp. Where no one but an admin
 * holds the right, it says so and reports no coverage: unverified, never "covered" (L1).
 * Nothing binding: no line.
 */
export function authorityCoverageWords(c: AuthorityCounts, scope: 'commitment' | 'workspace'): string | null {
  if (c.binding <= 0) return null;
  const lead = scope === 'commitment' ? 'Authority' : 'Authority across the Registry';
  const these = `${c.binding} binding ${plural(c.binding, 'assignment', 'assignments')}`;
  if (c.rightsConfigured === false && c.byHolder === 0) {
    return `${lead} unverified: no one but an admin holds the right to authorize assignments in this workspace, so there is no holder to check ${scope === 'commitment' ? 'its' : 'the'} ${these} against.`;
  }
  const parts = [`${c.byHolder} of ${these} authorized by a holder of the right`];
  if (c.byAdmin > 0) parts.push(`${c.byAdmin} by an admin`);
  if (c.noRightConfigured > 0) parts.push(`${c.noRightConfigured} unverified, by an admin when no one else held the right`);
  if (c.unstamped > 0) parts.push(`${c.unstamped} unverified, with no record of the right held`);
  return `${lead}: ${parts.join('; ')}.`;
}
